// ============================================================================
// SYNC STATUS MUTATION HOOK (Apollo GraphQL) FOR TIME ENTRIES
// ============================================================================

import { useMemo } from "react";
import { useMutation } from "@apollo/client/react";
import {
  UpdateTimeEntriesDocument,
  UpdateTimeEntriesMutation,
} from "@/src/gql/graphql";
import { nowIso } from "./graphql-meta";

type SyncStatusInput = {
  id: string;
  syncStatus: string;
  quickBooksId?: string;
  syncedAt?: string;
};

export function useUpdateTimeEntrySyncStatus() {
  const [mutate, result] = useMutation<UpdateTimeEntriesMutation>(UpdateTimeEntriesDocument);

  const mutateFn = useMemo(
    () => async ({ id, syncStatus, quickBooksId, syncedAt }: SyncStatusInput) => {
      const { data, error } = await mutate({
        variables: {
          id,
          input: {
            sync_status: syncStatus as "disapproved" | "failed" | "pending" | "synced",
            quickbooks_id: quickBooksId || null,
            synced_at: syncedAt || nowIso(),
            updated_at: nowIso(),
          },
        },
        errorPolicy: "all",
      });

      if (error) throw new Error(`GraphQL error: ${error.message}`);
      const updatedTimeEntry = data?.updatetime_entriesCollection?.records?.[0];
      if (!updatedTimeEntry) throw new Error("Failed to update sync status");
      return updatedTimeEntry;
    },
    [mutate]
  );

  return {
    mutate: mutateFn,
    isLoading: result.loading,
    error: result.error,
    isError: !!result.error,
    isSuccess: !result.loading && !result.error,
  };
}
